import { User } from 'oidc-client-ts';
import { meansSessionIsDead, oidcErrorCode } from './auth-config';

/**
 * Recuperacion de la renovacion silenciosa.
 *
 * `SilentRenewService` de oidc-client-ts reintenta UNICAMENTE los `ErrorTimeout`, cada 5 s. Ante
 * cualquier otro error levanta `silentRenewError` y se detiene, y el temporizador
 * `accessTokenExpiring` que lo habia disparado ya se ha cancelado: solo se rearma cuando se carga
 * un usuario nuevo, cosa que sin renovacion no va a ocurrir. O sea que un unico fallo deja la
 * pestana con un token que morira y sin ninguna forma de sustituirlo. Verificado igual en 2.2.3,
 * 2.5.0 y 3.5.0 con los ajustes que pone `auth-config`, asi que no es algo que arregle subir de
 * version. Con un matiz: `oidc-client-ts` **3.5.0** anade `maxSilentRenewTimeoutRetries`, que acota
 * los reintentos de `ErrorTimeout` en vez de dejarlos infinitos, y eso cambiaria esta rama. No es la
 * mayor 3 entera: la 3.4.1 todavia no lo declara, comprobado en el paquete publicado. Hoy no se fija,
 * y su propio `.d.ts` dice que sin fijar mantiene la conducta de siempre.
 *
 * El caso real que lo dispara es la suspension del portatil. Mientras el equipo duerme los
 * temporizadores no corren; al despertar, oidc-client-ts ve el vencimiento y lanza la renovacion de
 * inmediato, con la red todavia sin volver. Ese `fetch` falla con `TypeError`, que no es
 * `ErrorTimeout`, y entra por la rama sin reintento. El usuario sigue en la pestana donde estaba,
 * termina lo que tuviera a medias y todo responde 401.
 *
 * Aqui se rearma eso: reintentos con espera creciente, y ademas un intento inmediato cuando vuelve
 * la red o cuando el usuario vuelve a la pestana, que es exactamente el momento en que la sesion se
 * le ha quedado atras.
 */

/** Lo que hace falta para renovar. Lo aporta `session-guard`, con el `UserManager` de `AuthProvider`. */
export type SessionRenewer = () => Promise<User | null>;

export type RenewOutcome = { ok: true; user: User } | { ok: false; sessionDead: boolean; error: unknown };

const warn = (message: string, detail?: unknown) => console.warn(`session-recovery: ${message}`, detail ?? '');

/**
 * Espera entre intentos, en segundos, y despues `STEADY_RETRY_SECONDS` un numero acotado de veces.
 *
 * El techo existe porque una pestana olvidada con la sesion caida no puede quedarse llamando a
 * `/connect/authorize` para siempre. Se cuenta sobre la vida de la pagina, no sobre la tanda: un
 * disparador manual reinicia la escalera corta, pero no devuelve credito de los sostenidos.
 *
 * Agotados, lo que se acaba es el goteo largo: una pestana a la que nadie toca deja de llamar. Un
 * disparador posterior -- volver la red, volver el usuario -- si vuelve a recorrer la escalera corta
 * entera, que son cinco intentos en unos tres minutos, porque ahi ha cambiado algo. Y el camino del
 * 401 en axios no pasa por este techo en absoluto: lo unico que lo acota es el suelo desde el ultimo
 * fallo.
 */
const BACKOFF_SECONDS = [5, 15, 45, 120];
const STEADY_RETRY_SECONDS = 300;
const MAX_STEADY_RETRIES = 12;

/**
 * Dispersion del reintento sostenido, +-20%. Las tres aplicaciones comparten un solo identity, y
 * cuando vuelve de un despliegue todas las pestanas abandonadas reintentarian dentro de la misma
 * ventana de cinco minutos.
 */
const STEADY_JITTER = 0.2;

/**
 * Suelo desde el ultimo fallo. `retrySessionNow` cuelga de `focus`, `online` y `visibilitychange`:
 * sin esto, alguien haciendo alt-tab con identity caido emite un `signinSilent` por cada evento.
 * Vale tambien para el interceptor: si la renovacion acaba de fallar, un 401 no lanza otra, porque
 * la recuperacion de fondo ya se esta ocupando.
 */
const MIN_RETRY_AFTER_FAILURE_MS = 5000;

/** Techo del camino sincrono, el del interceptor de axios. Ver `renewSessionWithin`. */
export const INTERACTIVE_RENEW_TIMEOUT_MS = 5000;

/**
 * Techo duro de una renovacion, para que `inFlight` no se quede clavado.
 *
 * `silentRequestTimeoutInSeconds` solo acota la navegacion del iframe. Lo que viene despues no lleva
 * techo ninguno: `JsonService.getJson`, que es el GET a userinfo que `loadUserInfo: true` obliga a
 * hacer en cada renovacion, llama a `fetch` pelado, sin `AbortController`. `auth-config` ya se
 * defiende de esto en el arranque con `OVERALL_CHECK_TIMEOUT_MS`, y aqui hace la misma falta: con
 * identity aceptando la conexion y quedandose colgado -- un contenedor reiniciandose detras del
 * balanceador, que es el disparador citado en todo este arreglo -- una renovacion sin techo dejaba
 * `inFlight` ocupado y el calendario sin rearmar. O sea la recuperacion apagada para el resto de la
 * vida de la pagina, y justo en el escenario que esto viene a arreglar.
 */
const RENEW_HARD_TIMEOUT_MS = 30000;

/**
 * El `signinSilent` vigente. Se guarda en una ranura en vez de construir aqui un `UserManager`
 * propio: habria dos haciendo `signinSilent` a la vez sobre el mismo `userStore`, que es justo lo
 * que `auth-config` se cuida de no hacer.
 */
let renewer: SessionRenewer | null = null;

/**
 * De donde sale el error con el que se decide si la sesion esta muerta.
 *
 * NO se puede depender de que la promesa de `signinSilent` rechace, y el motivo no tiene fecha: el
 * envoltorio de `react-oidc-context` atrapa el error, despacha `ERROR` y **devuelve `null`** en vez
 * de rechazar. Clasificando por el rechazo no llegaria nunca un `login_required` por esa via: el
 * modal no saldria jamas y los reintentos no pararian nunca.
 *
 * El suelo esta medido, y **no es la mayor 3**: el `catch` no existe en la 2.2.2, que envuelve con un
 * `try/finally` a secas y deja que la promesa rechace, y esta desde la **2.3.0**, que entonces
 * envolvia seis navegadores, `signinSilent` entre ellos. El septimo, `signinResourceOwnerCredentials`,
 * entra en la 2.3.1 y el payload no lo llama. Contado en los paquetes publicados de 2.2.2, 2.3.0,
 * 2.3.1, 2.4.0 y 3.3.1. La 2.2.2 era la del harness que se borro en j2l-shared#131.
 *
 * Lo que si cambia entre la 2 y la 3 es la FORMA del error que se despacha: la 3 manda
 * `{ ...normalizeError(error), source, args }`, con el original dentro de `innerError`. Eso lo
 * absorbe `oidcErrorCode` en `auth-config`, no esto. Aqui **la clasificacion va por el error que
 * llega, no por como termine la promesa**, asi que este fichero no necesita saber en que version esta
 * la aplicacion que lo monta. Que es lo que se quiere de un fichero que se copia a las tres.
 *
 * `session-guard` entrega por aqui el ultimo error que haya visto, preferentemente el que le llega
 * por el evento `silentRenewError`, que es sincrono. Su `auth.error` sirve de respaldo, pero llega
 * un ciclo tarde: React despacha por el Scheduler, que es un macrotask, y esta clasificacion corre
 * en el microtask de la promesa.
 */
let authErrorSource: (() => unknown) | null = null;

/** Un unico intento en vuelo. Una rafaga de 401 comparte renovacion en lugar de estampidar. */
let inFlight: Promise<RenewOutcome> | null = null;

let retryTimer: ReturnType<typeof setTimeout> | undefined;
let attempt = 0;
let steadyRetries = 0;
let recovering = false;
let attemptRunning = false;
let lastFailureAt = 0;

/** La sesion ya no se puede renovar. Solo lo levanta `stopSessionRecovery`, al llegar un usuario. */
let sessionDead = false;

/** Si el usuario ya tiene el aviso delante. Se baja con `acknowledgeSessionDead`, no con el anterior. */
let sessionDeadNotified = false;

let onSessionDead: (() => void) | null = null;

export const registerSessionRenewer = (fn: SessionRenewer | null): void => {
  renewer = fn;
};

export const registerAuthErrorSource = (fn: (() => unknown) | null): void => {
  authErrorSource = fn;
};

export const setOnSessionDead = (fn: (() => void) | null): void => {
  onSessionDead = fn;
};

const clearTimer = () => {
  if (retryTimer != null) clearTimeout(retryTimer);
  retryTimer = undefined;
};

/**
 * Desarma los temporizadores y los intentos, y **no toca** lo que se sepa de la sesion.
 *
 * Esto es lo que quiere quien se va: un componente que se desmonta, o un efecto que se limpia. Que la
 * sesion este muerta no lo decide React, lo decide identity, y hasta que llegue un usuario nuevo lo
 * sigue estando aunque el arbol se haya vuelto a montar tres veces por el camino.
 *
 * Desarmar incluye `attemptRunning` y `inFlight`, y no es un detalle: si esto dejase algo por limpiar,
 * ese algo seria un estado del que la aplicacion no podria salir ni desmontando el componente ni
 * volviendo a entrar.
 */
export const suspendSessionRecovery = (): void => {
  clearTimer();
  attempt = 0;
  steadyRetries = 0;
  recovering = false;
  attemptRunning = false;
  inFlight = null;
};

/**
 * Se ha renovado, o ha llegado un usuario por otra via. Se desarma TODO, incluido lo que se sabia de la
 * sesion, porque ahora hay una viva.
 *
 * Solo lo llaman dos sitios: el `userLoaded` de `session-guard` y el intento que acaba bien. Lo colgaba
 * tambien la limpieza de un efecto, y ahi hacia dano: ese efecto depende de `hasSession`, asi que un
 * parpadeo de `auth.user` a `null` borraba el «la sesion esta muerta» sin que hubiera llegado nadie
 * (j2l-shared#124). Para eso esta `suspendSessionRecovery`.
 *
 * Los tests la usan tambien, para que no puedan partir nunca de un estado mas limpio que el que la
 * produccion sabe alcanzar.
 */
export const stopSessionRecovery = (): void => {
  suspendSessionRecovery();
  lastFailureAt = 0;
  sessionDead = false;
  sessionDeadNotified = false;
};

/**
 * La sesion viva ya no existe, asi que no hay nada que reintentar. El aviso sale una sola vez: aqui
 * se entra desde el temporizador y tambien desde el interceptor de axios, y repintarselo al usuario
 * en cada peticion fallida seria peor que el propio fallo.
 */
export const notifySessionDead = (): void => {
  clearTimer();
  attempt = 0;
  steadyRetries = 0;
  recovering = false;
  sessionDead = true;
  if (sessionDeadNotified) return;
  sessionDeadNotified = true;
  warn('la sesion ya no se puede renovar, hay que volver a entrar');
  // El aviso es de quien lo registre, o sea codigo de React. Si lanzase, se llevaria por delante la
  // cadena de promesas desde la que se entra aqui y dejaria el calendario sin reprogramar.
  try {
    onSessionDead?.();
  } catch (error) {
    warn('el aviso de sesion muerta ha fallado', error);
  }
};

/**
 * El usuario ha cerrado el aviso con «Ahora no». No se reintenta nada -- la sesion sigue muerta --
 * pero se baja la marca para que el siguiente 401 vuelva a avisarle. Sin esto, cerrar el modal una
 * vez lo hacia desaparecer para el resto de la vida de la pagina y dejaba al usuario igual que antes
 * de este arreglo, solo que habiendo visto un aviso.
 */
export const acknowledgeSessionDead = (): void => {
  sessionDeadNotified = false;
};

/** Si la sesion ya se dio por muerta. Lo consulta axios para no lanzar renovaciones condenadas. */
export const isSessionDead = (): boolean => sessionDead;

/**
 * Si tiene sentido intentar una renovacion ahora mismo. Lo consulta el interceptor: tras un fallo
 * reciente la recuperacion de fondo ya esta en ello, y un chorro de 401 secuenciales no debe
 * convertirse en un chorro de `signinSilent`.
 */
export const canRenewNow = (): boolean => !sessionDead && Date.now() - lastFailureAt >= MIN_RETRY_AFTER_FAILURE_MS;

/**
 * `invalid_grant` cuenta como sesion muerta AQUI y no en `SESSION_IS_DEAD_ERRORS`, que es una
 * constante compartida.
 *
 * Hoy ninguna SPA pide `offline_access`, pero los cuatro clientes estan sembrados con
 * `AllowOfflineAccess = true` y `getOidcConfig` acepta `scopes` abierto: el dia que alguien lo anada,
 * un refresh token revocado vuelve asi, y sin esto se tomaria por un fallo pasajero y se reintentaria
 * cada cinco minutos sin avisar nunca. Metiendolo en la constante compartida se ampliaba tambien
 * `checkSession`, donde un error de sesion muerta borra el usuario al arrancar: ahi si es alcanzable
 * un `invalid_grant` por un code caducado o reusado, y le costaria la sesion a quien no debia.
 */
const renewalIsHopeless = (error: unknown): boolean => meansSessionIsDead(error) || oidcErrorCode(error) === 'invalid_grant';

const classify = (error: unknown): boolean => renewalIsHopeless(error) || renewalIsHopeless(authErrorSource?.());

/**
 * Renueva, o dice por que no ha podido. Nunca lanza: los llamantes son un interceptor de axios y un
 * temporizador, y en ninguno de los dos sitios una excepcion suelta lleva a nada bueno.
 *
 * Lleva techo duro propio. Si vence, `inFlight` se libera y la promesa de debajo se queda corriendo
 * sola: si acaba bien, `signinSilent` habra guardado el usuario igualmente y el `userLoaded` de
 * `session-guard` recogera el resultado.
 */
export const renewSession = (): Promise<RenewOutcome> => {
  if (renewer == null) {
    warn('no hay renovador registrado, no hay sesion que renovar');
    return Promise.resolve({ ok: false, sessionDead: false, error: new Error('no renewer') });
  }

  if (sessionDead) return Promise.resolve({ ok: false, sessionDead: true, error: new Error('sesion muerta') });

  if (inFlight != null) return inFlight;

  const current = renewer;

  // `Promise.resolve().then(...)` y no `renewer()` a secas: si el renovador lanzase de forma
  // sincrona, la excepcion saldria de aqui antes de montar la carrera y `runAttempt` se quedaria sin
  // reprogramar. Asi cualquier fallo llega por la via normal.
  const attempted: Promise<RenewOutcome> = Promise.resolve()
    .then(() => current())
    .then<RenewOutcome>((user) =>
      // Un `null` es como llega un error: el envoltorio lo atrapa y devuelve `null` en vez de
      // rechazar. Por eso se clasifica igual que un rechazo. Vale desde `react-oidc-context` 2.3.0.
      user != null ? { ok: true, user } : { ok: false, sessionDead: classify(undefined), error: new Error('signinSilent devolvio null') }
    )
    .catch<RenewOutcome>((error) => ({ ok: false, sessionDead: classify(error), error }));

  let timer: ReturnType<typeof setTimeout> | undefined;
  const hardDeadline = new Promise<RenewOutcome>((resolve) => {
    timer = setTimeout(() => {
      warn(`la renovacion lleva mas de ${RENEW_HARD_TIMEOUT_MS} ms sin contestar, se abandona este intento`);
      resolve({ ok: false, sessionDead: false, error: new Error('renovacion colgada') });
    }, RENEW_HARD_TIMEOUT_MS);
  });

  inFlight = Promise.race([attempted, hardDeadline])
    .then((outcome) => {
      if (!outcome.ok) {
        lastFailureAt = Date.now();
        if (outcome.sessionDead) notifySessionDead();
      }
      return outcome;
    })
    .finally(() => {
      if (timer != null) clearTimeout(timer);
      inFlight = null;
    });

  return inFlight;
};

/**
 * La renovacion, pero con un techo de espera mas corto.
 *
 * El interceptor de axios necesita esto: sin techo, un 401 que no tiene nada que ver con la
 * caducidad -- permisos, un recurso ajeno -- se quedaria esperando lo que tarde la renovacion, con
 * el boton girando. La de debajo NO se cancela: sigue su curso dentro de su propio techo duro.
 */
export const renewSessionWithin = (timeoutMs: number): Promise<RenewOutcome> => {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const deadline = new Promise<RenewOutcome>((resolve) => {
    timer = setTimeout(() => resolve({ ok: false, sessionDead: false, error: new Error('renovacion fuera de plazo') }), timeoutMs);
  });

  return Promise.race([renewSession(), deadline]).finally(() => {
    if (timer != null) clearTimeout(timer);
  });
};

const scheduleNext = (): void => {
  if (!recovering) return;

  let wait = BACKOFF_SECONDS[attempt];

  if (wait == null) {
    if (steadyRetries >= MAX_STEADY_RETRIES) {
      // Se apagan los temporizadores, pero `recovering` se queda en pie para que `online`, `focus` y
      // `visibilitychange` puedan seguir intentandolo.
      clearTimer();
      warn('agotados los reintentos por tiempo, se sigue a la espera de que vuelva la red o el usuario');
      return;
    }
    steadyRetries += 1;
    wait = STEADY_RETRY_SECONDS * (1 + (Math.random() * 2 - 1) * STEADY_JITTER);
  }

  attempt += 1;
  clearTimer();
  retryTimer = setTimeout(() => void runAttempt(), wait * 1000);
};

const runAttempt = async (): Promise<void> => {
  // Un disparador puede llegar mientras ya hay un intento en curso. Sin esto, el mismo fallo
  // avanzaria el calendario dos veces.
  if (attemptRunning) return;
  attemptRunning = true;

  try {
    const outcome = await renewSession();

    if (outcome.ok) {
      stopSessionRecovery();
      return;
    }

    // `notifySessionDead` ya se ha encargado desde `renewSession`, incluido desarmar esto.
    if (outcome.sessionDead) return;

    warn('renovacion fallida, se reintentara', outcome.error);
    scheduleNext();
  } catch (error) {
    // `renewSession` promete no lanzar, pero si algun dia lo hiciera, dejar la escalera muda seria
    // mucho peor que reprogramar de mas.
    warn('el intento de renovacion ha lanzado, se reintentara igualmente', error);
    scheduleNext();
  } finally {
    attemptRunning = false;
  }
};

/**
 * Arranca la recuperacion. Idempotente: llamarla dos veces no duplica temporizadores ni reinicia la
 * cuenta de intentos, que es lo que pasaria si `silentRenewError` llegase mas de una vez.
 */
export const startSessionRecovery = (): void => {
  if (recovering || sessionDead) return;
  recovering = true;
  attempt = 0;
  scheduleNext();
};

/** Un intento ya, saltandose lo que quedara de espera. */
export const retrySessionNow = (): void => {
  if (!recovering || sessionDead) return;
  if (Date.now() - lastFailureAt < MIN_RETRY_AFTER_FAILURE_MS) return;
  clearTimer();
  // Ha cambiado algo (vuelve la red, vuelve el usuario), asi que la escalera corta empieza de nuevo.
  // `steadyRetries` NO se reinicia: es el techo de la vida de la pagina, y devolverle credito aqui
  // convertiria el alt-tab en una forma de machacar identity sin limite.
  attempt = 0;
  void runAttempt();
};

/**
 * Los disparadores que cubren el caso reportado. El temporizador solo sabe de tiempo; estos tres
 * saben de que ha cambiado algo que hace que ahora si tenga sentido intentarlo.
 *
 * Devuelve la funcion que los quita.
 */
export const installSessionRecoveryTriggers = (): (() => void) => {
  const onVisible = () => {
    if (document.visibilityState === 'visible') retrySessionNow();
  };

  window.addEventListener('online', retrySessionNow);
  window.addEventListener('focus', retrySessionNow);
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    window.removeEventListener('online', retrySessionNow);
    window.removeEventListener('focus', retrySessionNow);
    document.removeEventListener('visibilitychange', onVisible);
  };
};

/** Solo para los tests. Deja el modulo como recien cargado. */
export const resetSessionRecoveryForTests = (): void => {
  stopSessionRecovery();
  renewer = null;
  authErrorSource = null;
  onSessionDead = null;
};
